import type Hls from 'hls.js'
import { Loader2, Play } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAccessToken } from '@/api/client'
import type { VideoRef } from '../types'

function formatDuration(seconds: number | null): string | null {
  if (seconds === null) return null
  const s = Math.round(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`
}

/**
 * Plays a streaming video (HLS). Playlists come from our API with the session token; segments are
 * signed storage links fetched without it. hls.js is only loaded once someone presses play.
 */
export function VideoPlayer({ video, title }: { video: VideoRef; title: string }) {
  const { t } = useTranslation('lms')
  const element = useRef<HTMLVideoElement>(null)
  const [started, setStarted] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!started || !element.current) return
    const el = element.current
    let hls: Hls | null = null
    let cancelled = false
    void import('hls.js').then(({ default: HlsClass }) => {
      if (cancelled) return
      if (HlsClass.isSupported()) {
        hls = new HlsClass({
          xhrSetup: (xhr, url) => {
            // Only our API needs the session; storage links are already signed.
            const token = getAccessToken()
            if (token && new URL(url, window.location.href).pathname.startsWith('/api/')) {
              xhr.setRequestHeader('Authorization', `Bearer ${token}`)
            }
          },
        })
        hls.on(HlsClass.Events.ERROR, (_e, data) => {
          if (data.fatal) setError(true)
        })
        hls.loadSource(video.playlistUrl)
        hls.attachMedia(el)
        void el.play().catch(() => undefined)
      } else {
        setError(true)
      }
    })
    return () => {
      cancelled = true
      hls?.destroy()
    }
  }, [started, video.playlistUrl])

  if (error) {
    return (
      <p className="rounded-control bg-danger-50 px-3 py-2 text-sm text-danger-700">
        {t('The video could not be played. Try again, or use another browser.')}
      </p>
    )
  }

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-control bg-black">
      {started ? (
        <video
          ref={element}
          className="size-full"
          controls
          playsInline
          poster={video.thumbnailUrl ?? undefined}
          aria-label={title}
        >
          {/* Teachers' videos have no caption files yet. */}
          <track kind="captions" />
        </video>
      ) : (
        <button
          type="button"
          onClick={() => setStarted(true)}
          className="group absolute inset-0 grid place-items-center"
          aria-label={t('Play {{title}}', { title })}
        >
          {video.thumbnailUrl ? (
            <img
              src={video.thumbnailUrl}
              alt=""
              className="absolute inset-0 size-full object-cover opacity-90"
            />
          ) : null}
          <span className="relative grid size-16 place-items-center rounded-full bg-white/90 text-brand-700 shadow-raised transition group-hover:scale-105">
            <Play className="ml-1 size-7" aria-hidden="true" />
          </span>
          {formatDuration(video.durationSeconds) ? (
            <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white tabular">
              {formatDuration(video.durationSeconds)}
            </span>
          ) : null}
        </button>
      )}
    </div>
  )
}

/** A streaming video in a material: the player once ready, otherwise where it stands. */
export function VideoBlock({ video, title }: { video: VideoRef; title: string }) {
  const { t } = useTranslation('lms')
  if (video.status === 'ready') return <VideoPlayer video={video} title={title} />
  if (video.status === 'failed') {
    return (
      <p className="rounded-control bg-danger-50 px-3 py-2 text-sm text-danger-700">
        {video.error ?? t('The video could not be converted.')}
      </p>
    )
  }
  return (
    <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-control border border-dashed border-line bg-surface-muted/50 text-sm text-muted">
      <Loader2 className="size-6 animate-spin text-brand-600" aria-hidden="true" />
      {t('The video is being prepared for streaming…')}
    </div>
  )
}
